import React, { useMemo, useState } from "react";
import { executeMutation } from "../../../graphql/client";
import "./FlightBoard.css";

// Shared flight-deal board: time-window filter chips (24h default),
// favorite-region chips (hidden set persists in localStorage), the
// 🏆 Best Price by Region summary, and one price-sorted table per
// region group. Used by BOTH the admin Fly Scraper tab and the
// member-facing Flights app — pass the price-sorted `items` array
// from the flyStatus/flyFlights endpoints. Display-only: run
// controls, logging, and status live with the admin tab, not here.

const MAX_REGION_ROWS = 50;

// Static region-id map, mirrored from fly/scrape/handlers/getRegions.js
// (the scraper stores only the numeric regionDestination on each row).
const REGION_NAMES = {
  1: "Middle East",
  2: "Europe",
  3: "Oceania",
  4: "South East Asia",
  5: "Central America",
  6: "Hawaii",
  7: "South America",
  8: "Caribbean",
  16: "Rocky Mountains",
  29: "United States",
  30: "Mexico",
};

export const regionName = (id) =>
  REGION_NAMES[id] || (id != null ? `Region ${id}` : "Unknown");

const HOURS_CHOICES = [
  { label: "1h", value: 1 },
  { label: "12h", value: 12 },
  { label: "24h", value: 24 },
  { label: "48h", value: 48 },
  { label: "72h", value: 72 },
  { label: "All", value: 0 },
];

// Same localStorage keys the admin tab always used, so an admin's
// favorites carry over; regular users just get their own browser's.
const HOURS_LS_KEY = "flyTab-foundWithinHours";
const HIDDEN_REGIONS_LS_KEY = "flyTab-hiddenRegions";

// "2026-9-15" -> "9-15": small screens can't afford the year, which
// lives in the cell tooltip instead.
const shortDate = (d) => {
  const m = /^\d{4}-(\d{1,2})-(\d{1,2})$/.exec(String(d || ""));
  return m ? `${m[1]}-${m[2]}` : String(d || "");
};

const fmtDates = (f) => {
  if (f.startDate && f.endDate)
    return `${shortDate(f.startDate)} – ${shortDate(f.endDate)}`;
  return shortDate(f.startDate) || f.rawDates || "—";
};

const fullDates = (f) =>
  f.startDate ? `${f.startDate}${f.endDate ? ` – ${f.endDate}` : ""}` : "";

// Just the number ("0", "1", "2") — column stays narrow on phones.
const fmtStops = (stops) => {
  if (stops == null || stops === "") return "—";
  const n = Number(stops);
  return Number.isFinite(n) ? String(n) : String(stops);
};

const fmtAgo = (raw) => {
  if (!raw) return "—";
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return "—";
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

// Same link the local dashboard uses: a Google search for the route +
// start date, which lands on Google's flight results for those dates.
const flightSearchUrl = (f) =>
  `https://www.google.com/search?q=${encodeURIComponent(
    `${f.origin || ""} to ${f.destination || ""} ${f.startDate || ""}`.trim()
  )}`;

// Fire-and-forget click tracking — must NEVER block or break the
// click-through (the link opens in a new tab regardless).
const trackClick = (f) => {
  try {
    executeMutation(
      `mutation RecordFlightClick($input: FlightClickInput!) {
        recordFlightClick(input: $input)
      }`,
      {
        input: {
          origin: f.origin || null,
          destination: f.destination || null,
          price: f.price != null ? Number(f.price) : null,
          startDate: f.startDate || null,
          endDate: f.endDate || null,
          regionDestination:
            f.regionDestination != null
              ? Number(f.regionDestination)
              : null,
          stops: f.stops != null ? Number(f.stops) : null,
        },
      }
    ).catch(() => {});
  } catch {
    // tracking is best-effort
  }
};

const PriceLink = ({ f }) =>
  f.price < 999999 ? (
    <a
      className="flyboard__price-link"
      href={flightSearchUrl(f)}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${f.origin} → ${f.destination} on Google`}
      onClick={() => trackClick(f)}
    >
      ${f.price}
    </a>
  ) : (
    <span>—</span>
  );

const loadHiddenRegions = () => {
  try {
    const raw = window.localStorage.getItem(HIDDEN_REGIONS_LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};

const FlightBoard = ({ items }) => {
  const allItems = useMemo(() => items || [], [items]);

  const [hours, setHours] = useState(() => {
    const stored = parseInt(
      window.localStorage.getItem(HOURS_LS_KEY) || "24",
      10
    );
    return Number.isFinite(stored) ? stored : 24;
  });
  const [hiddenRegions, setHiddenRegions] = useState(loadHiddenRegions);

  const setHoursPersist = (h) => {
    setHours(h);
    try {
      window.localStorage.setItem(HOURS_LS_KEY, String(h));
    } catch {
      // localStorage unavailable — filter still works for the session
    }
  };

  const toggleRegion = (id) => {
    setHiddenRegions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(
          HIDDEN_REGIONS_LS_KEY,
          JSON.stringify([...next])
        );
      } catch {
        // non-fatal
      }
      return next;
    });
  };

  // Time-window filter (region-independent, so chip counts reflect
  // what each region has in the current window).
  const windowItems = useMemo(() => {
    if (!hours) return allItems;
    const cutoff = Date.now() - hours * 3600 * 1000;
    return allItems.filter((f) => {
      const t = f.updatedAt ? new Date(f.updatedAt).getTime() : NaN;
      return Number.isFinite(t) && t >= cutoff;
    });
  }, [allItems, hours]);

  // One row per origin+destination combo: the scraper keeps the same
  // route across several date windows (JAX → London shows up once per
  // window), but the board shows only the MOST RECENT find for each
  // route. Re-sorted by price afterwards since the survivor isn't
  // necessarily the cheapest duplicate.
  const dedupedItems = useMemo(() => {
    const byRoute = new Map();
    for (const f of windowItems) {
      // Normalized key — stray whitespace/case differences between
      // scrapes must not resurrect duplicates.
      const key = `${String(f.origin || "")
        .trim()
        .toLowerCase()}|${String(f.destination || "")
        .trim()
        .toLowerCase()}`;
      const cur = byRoute.get(key);
      const t = f.updatedAt ? new Date(f.updatedAt).getTime() : 0;
      const curT =
        cur && cur.updatedAt ? new Date(cur.updatedAt).getTime() : 0;
      if (!cur || t > curT) byRoute.set(key, f);
    }
    return [...byRoute.values()].sort((a, b) => a.price - b.price);
  }, [windowItems]);

  const regionsInWindow = useMemo(() => {
    const counts = new Map();
    for (const f of dedupedItems) {
      const id = f.regionDestination;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([id, count]) => ({ id, count, name: regionName(id) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [dedupedItems]);

  const visibleItems = useMemo(
    () =>
      dedupedItems.filter((f) => !hiddenRegions.has(f.regionDestination)),
    [dedupedItems, hiddenRegions]
  );

  // 🏆 cheapest flight per visible region in the window.
  const bestByRegion = useMemo(() => {
    const best = new Map();
    for (const f of visibleItems) {
      const cur = best.get(f.regionDestination);
      if (!cur || f.price < cur.price) best.set(f.regionDestination, f);
    }
    return [...best.values()].sort((a, b) => a.price - b.price);
  }, [visibleItems]);

  // Per-region tables (like the local dashboard): group the visible,
  // already price-sorted flights by region, cheapest region first.
  const regionGroups = useMemo(() => {
    const groups = new Map();
    for (const f of visibleItems) {
      if (!groups.has(f.regionDestination)) {
        groups.set(f.regionDestination, []);
      }
      groups.get(f.regionDestination).push(f);
    }
    return [...groups.entries()]
      .map(([id, items2]) => ({ id, name: regionName(id), items: items2 }))
      .sort((a, b) => a.items[0].price - b.items[0].price);
  }, [visibleItems]);

  if (allItems.length === 0) return null;

  return (
    <div className="flyboard">
      <div className="flyboard__filters">
        <div className="flyboard__filter-group">
          <span className="flyboard__filter-label">Found within</span>
          {HOURS_CHOICES.map((c) => (
            <button
              key={c.label}
              type="button"
              className={`flyboard__chip ${
                hours === c.value ? "flyboard__chip--on" : ""
              }`}
              onClick={() => setHoursPersist(c.value)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="flyboard__filter-group">
          <span className="flyboard__filter-label">Regions</span>
          {regionsInWindow.map((r) => {
            const on = !hiddenRegions.has(r.id);
            return (
              <button
                key={String(r.id)}
                type="button"
                className={`flyboard__chip ${
                  on ? "flyboard__chip--on" : "flyboard__chip--off"
                }`}
                onClick={() => toggleRegion(r.id)}
                title={on ? "Click to hide" : "Click to show"}
              >
                {r.name} ({r.count})
              </button>
            );
          })}
        </div>
      </div>

      {windowItems.length === 0 ? (
        <div className="flyboard__warn">
          No flights found in the selected window — try a wider one.
        </div>
      ) : (
        <>
          <h3 className="flyboard__section">🏆 Best Price by Region</h3>
          <div className="flyboard__table-wrap">
            <table className="flyboard__table">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Price</th>
                  <th>Destination</th>
                  <th>From</th>
                  <th>Dates</th>
                  <th>Stops</th>
                  <th>Seen</th>
                </tr>
              </thead>
              <tbody>
                {bestByRegion.map((f) => (
                  <tr key={`best-${f.regionDestination}`}>
                    <td>{regionName(f.regionDestination)}</td>
                    <td className="flyboard__price">
                      <PriceLink f={f} />
                    </td>
                    <td>{f.destination || "—"}</td>
                    <td>{f.origin || "—"}</td>
                    <td className="flyboard__dates" title={fullDates(f)}>
                      {fmtDates(f)}
                    </td>
                    <td className="flyboard__muted">{fmtStops(f.stops)}</td>
                    <td className="flyboard__muted">{fmtAgo(f.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {regionGroups.map((g) => (
            <React.Fragment key={String(g.id)}>
              <h3 className="flyboard__section">
                {g.name} ({g.items.length}
                {g.items.length > MAX_REGION_ROWS
                  ? `, showing cheapest ${MAX_REGION_ROWS}`
                  : ""}
                )
              </h3>
              <div className="flyboard__table-wrap">
                <table className="flyboard__table">
                  <thead>
                    <tr>
                      <th>From</th>
                      <th>To</th>
                      <th>Price</th>
                      <th>Dates</th>
                      <th>Stops</th>
                      <th>Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.slice(0, MAX_REGION_ROWS).map((f, i) => (
                      <tr key={`${f.origin}-${f.destination}-${i}`}>
                        <td>{f.origin || "—"}</td>
                        <td>{f.destination || "—"}</td>
                        <td className="flyboard__price">
                          <PriceLink f={f} />
                        </td>
                        <td className="flyboard__dates" title={fullDates(f)}>
                          {fmtDates(f)}
                        </td>
                        <td className="flyboard__muted">
                          {fmtStops(f.stops)}
                        </td>
                        <td className="flyboard__muted">
                          {fmtAgo(f.updatedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </React.Fragment>
          ))}
        </>
      )}
    </div>
  );
};

export default FlightBoard;
